import { KeyboardEvent } from 'react';
import Downshift, {
	DownshiftProps,
	DownshiftState,
	StateChangeOptions,
} from 'downshift';
import { AutoSuggestRoot } from './AutoSuggestRoot';
import {
	getItemDisplayValueDefault,
	getItemKeyDefault,
	getItemToStringDefault,
	IDropdownItem,
	IGetItemKey,
} from '../DropdownList';

export interface IAutoSuggestInputProps<
	TItemType extends IDropdownItem = IDropdownItem,
> extends DownshiftProps<TItemType> {
	items: TItemType[];
	placeholder?: string;
	ariaLabel?: string;
	itemToKey?: IGetItemKey;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	itemToString?: (item: any | null) => string;
	itemToDisplayValue?: IGetItemKey;
	onSelect?: (item: TItemType | null) => void;
	onInputValueChange?: (inputValue: string) => void;
	stateReducer?: (
		state: DownshiftState<TItemType>,
		changes: StateChangeOptions<TItemType>,
	) => StateChangeOptions<TItemType>;
	onKeyUp?: (event: KeyboardEvent<HTMLInputElement>) => void;
	initialInputValue?: string;
	disabled?: boolean;
	invalid?: boolean;
	required?: boolean;
	placeholderAsLabel?: boolean;
	onClearSearch?: () => void;
}

export function AutoSuggestInput<
	TItemType extends IDropdownItem = IDropdownItem,
>({
	placeholder,
	ariaLabel,
	items,
	itemToKey,
	itemToString,
	itemToDisplayValue,
	onKeyUp,
	disabled,
	invalid,
	required,
	placeholderAsLabel,
	onClearSearch,
	...rest
}: IAutoSuggestInputProps<TItemType>) {
	const getItemKey = itemToKey || getItemKeyDefault;
	const getItemDisplayValue = itemToDisplayValue || getItemDisplayValueDefault;
	const getItemToString = itemToString || getItemToStringDefault;

	return (
		<Downshift
			itemToString={getItemToString}
			id="downshift-autosuggest"
			{...rest}
		>
			{({ getRootProps, ...props }) => (
				<AutoSuggestRoot
					{...getRootProps({ refKey: 'innerRef' })}
					getItemKey={getItemKey}
					getItemDisplayValue={getItemDisplayValue}
					placeholder={placeholder}
					ariaLabel={ariaLabel ?? placeholder}
					items={items}
					onKeyUp={onKeyUp}
					disabled={disabled}
					invalid={invalid}
					required={required}
					placeholderAsLabel={placeholderAsLabel}
					onClearSearch={onClearSearch}
					{...props}
				/>
			)}
		</Downshift>
	);
}
